import {
  PACKAGE_TYPES,
  IMO_CLASS_OPTIONS,
  STATUS_STEPS,
  PreviewableExtensions,
  CURRENCIES,
} from "./formOptions";
import { translateValidation } from "../utils/translateValidation";

export const GOOGLE_PLACES_API_KEY =
  import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY || "";

export const INCOTERMS_OPTIONS = [
  { value: "EXW", label: "EXW" },
  { value: "FCA", label: "FCA" },
  { value: "CPT", label: "CPT" },
  { value: "CIP", label: "CIP" },
  { value: "DAP", label: "DAP" },
  { value: "DPU", label: "DPU" },
  { value: "DDP", label: "DDP" },
  { value: "FAS", label: "FAS" },
  { value: "FOB", label: "FOB" },
  { value: "CFR", label: "CFR" },
  { value: "CIF", label: "CIF" },
];

export const LOADTYPE_OPTIONS = [
  { value: "Full Load", label: "Full Load" },
  { value: "Partial Load", label: "Partial Load" },
];

export const TRANSPORT_MODE_OPTIONS = [
  { value: "ROAD", label: "Road" },
  { value: "SEA", label: "Sea" },
  { value: "AIR", label: "Air" },
];

// FAS, FOB, CFR, CIF are maritime-only — filtered out of the incoterms
// dropdown unless transportMode is SEA.
export const MARITIME_INCOTERMS = ["FAS", "FOB", "CFR", "CIF"];

// The stored loadType values never change ("Full Load" / "Partial Load");
// only the labels shown per transport mode differ:
// Full/Partial (ROAD) -> FCL/LCL (SEA) -> FTL/LTL (AIR).
export const LOADTYPE_OPTIONS_BY_MODE: Record<
  string,
  { value: string; label: string }[]
> = {
  ROAD: LOADTYPE_OPTIONS,
  SEA: [
    { value: "Full Load", label: "FCL" },
    { value: "Partial Load", label: "LCL" },
  ],
  AIR: [
    { value: "Full Load", label: "FTL" },
    { value: "Partial Load", label: "LTL" },
  ],
};

export const BL_TYPE_OPTIONS = [
  { value: "ORIGINAL", label: "Original" },
  { value: "SEAWAY", label: "Seaway Bill (non-negotiable)" },
  { value: "TELEX", label: "Telex Release / Express" },
];

// No. of original B/Ls — printed on the HBL as THREE (3) / TWO (2) / ONE (1).
export const NUMBER_OF_ORIGINALS_OPTIONS = [
  { value: "3", label: "Three (3)" },
  { value: "2", label: "Two (2)" },
  { value: "1", label: "One (1)" },
];

// Freight payment terms — Freight & charges box on the B/L.
export const FREIGHT_PAYMENT_TERMS_OPTIONS = [
  { value: "Prepaid", label: "Prepaid" },
  { value: "Collect", label: "Collect" },
];

// B/L Remark placement on the Bill of Lading. body_free is the catch-all /
// default used for anything that doesn't map to a specific box.
export const BL_REMARK_LOCATION_OPTIONS = [
  { value: "description_of_goods", label: "Description of Goods" },
  { value: "freight_charges", label: "Freight & Charges" },
  { value: "customs_declaration", label: "Customs Declaration" },
  { value: "marks_and_numbers", label: "Marks & Numbers" },
  { value: "below_freight_details", label: "Below Freight Details" },
  { value: "special_instructions", label: "Special Instructions" },
  { value: "container_notes", label: "Container Row Notes" },
  { value: "notify_party", label: "Notify Party Additions" },
  { value: "footer", label: "Footer / Signature Area" },
  { value: "body_free", label: "Body (Free Placement)" },
];

export const CONTAINER_TYPE_OPTIONS = [
  { value: "20DRY", label: "20' Dry (standard)" },
  { value: "40DRY", label: "40' Dry (standard)" },
  { value: "40HC", label: "40' High Cube" },
  { value: "45HC", label: "45' High Cube" },
  { value: "20REEFER", label: "20' Reefer (refrigerated)" },
  { value: "40REEFER", label: "40' Reefer" },
  { value: "20OT", label: "20' Open Top" },
  { value: "40OT", label: "40' Open Top" },
  { value: "20FR", label: "20' Flat Rack" },
  { value: "40FR", label: "40' Flat Rack" },
  { value: "20TANK", label: "20' Tank" },
  { value: "ISOTANK", label: "ISO Tank" },
];

export const DEFAULT_CARGO_ITEM = {
  quantity: 1,
  type: "EUR",
  weightKg: 0,
  volumeM3: 0,
  ldm: 0,
  length: 0,
  width: 0,
  height: 0,
  description: "",
  marks: "",
} as const;

export const DEFAULT_ADDRESS = {
  street: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
};

export const DEFAULT_PARTY = {
  companyName: "",
  registerationCode: "",
  phoneNumber: "",
  emailAddress: "",
  address: DEFAULT_ADDRESS,
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const getErrorMessage = (error: any): string | undefined => {
  if (error && typeof error === "object" && "message" in error) {
    return translateValidation(error.message);
  }
  return undefined;
};

export {
  PACKAGE_TYPES,
  IMO_CLASS_OPTIONS,
  STATUS_STEPS,
  PreviewableExtensions,
  CURRENCIES,
};
